import { RefCallback, useRef, useState } from 'react';

import useIntersection from 'src/hooks/useIntersection';

type ScrollPosition = 'TOP' | 'BOTTOM' | 'SCROLLING';

interface UseScrollBoxProps {
  rootMargin?: string;
}

/**
 * Defines the return type for the useScrollBox hook.
 */
export interface UseScrollBoxReturn {
  /** Ref callback to attach to the top sentinel element. */
  topSentinel: RefCallback<HTMLElement>;
  /** Ref callback to attach to the bottom sentinel element. */
  bottomSentinel: RefCallback<HTMLElement>;
  /**
   * The current scroll position:
   * - 'TOP': Scrolled to the top.
   * - 'BOTTOM': Scrolled to the bottom.
   * - 'SCROLLING': Scrolling somewhere in the middle.
   * - `undefined`: Content fits within the container without scrolling.
   */
  scrollPosition: ScrollPosition | null;
}

/**
 * 컨테이너 내의 스크롤 위치를 추적하는 커스텀 훅입니다.
 * `useIntersection` 훅을 사용하여 상단 및 하단 센티넬 요소의 가시성을 감지합니다.
 * 스크롤 가능한 콘텐츠의 상단과 하단에 각각 `topSentinel`과 `bottomSentinel` ref를 연결해야 합니다.
 *
 * @returns {UseScrollBoxReturn} 센티넬 요소에 연결할 ref 콜백 함수들과 현재 스크롤 위치 상태를 포함하는 객체.
 */
export default function useScrollBox({
  rootMargin = '0px',
}: UseScrollBoxProps): UseScrollBoxReturn {
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition | null>(null);

  const isBottomIntersectingRef = useRef(false);
  const isTopIntersectingRef = useRef(false);

  const updateScrollPosition = () => {
    const isBottom = isBottomIntersectingRef.current;
    const isTop = isTopIntersectingRef.current;

    if (isTop && isBottom) {
      setScrollPosition(null);
    } else if (isBottom) {
      setScrollPosition('BOTTOM');
    } else if (isTop) {
      setScrollPosition('TOP');
    } else {
      setScrollPosition('SCROLLING');
    }
  };

  const { observerRef: bottomSentinel } = useIntersection(
    ([entry]) => {
      isBottomIntersectingRef.current = entry.isIntersecting;
      updateScrollPosition();
    },
    {
      rootMargin,
    }
  );

  const { observerRef: topSentinel } = useIntersection(
    ([entry]) => {
      isTopIntersectingRef.current = entry.isIntersecting;
      updateScrollPosition();
    },
    {
      rootMargin,
    }
  );

  return { topSentinel, bottomSentinel, scrollPosition };
}
